import { useEffect, useRef, useState, type ReactNode } from "react";
import { useCouvre } from "./messager.js";
import type { SessionSummary } from "@hoa/protocol";
import { Moai } from "./Moai.js";
import { reduireImage } from "./photo.js";
import { baliseVoulue, ecrireBalise } from "./balise.js";
import type { SkinId } from "./skins.js";
import type { SoireePassee } from "./historique.js";
import { ecrirePreference, LIBELLES, lirePreferences, type Sorte } from "./prevenir.js";
import { ESPACES_HUMAINS } from "../espaces.js";

/** Les mêmes réglages, dits pour une table (Espaces Humains). */
const LIBELLES_TABLE: Partial<Record<Sorte, string>> = {
  part: "Le chat de ma table s'ouvre",
  annulee: "Ma table est annulée",
};
import { EtapesAVenir, libelleSession, type Planning } from "./ecrans2.js";
import { Avatar, Barre, BoutonMoi, Bulle, Croix, Ecran, Etape, Feuille, Fleur, Libelle, PastillePoints, Parole, Retour, heure, jour, pluriel } from "./ui.js";

/* ------------------------------------------------------------------ */
/* Première ouverture : une conversation avec Hoa (planche H-Intro)     */
/* ------------------------------------------------------------------ */

type ChoixLieu = "localiser" | "arrondissement";

/*
 * Les messages de Hoa, dans l'ordre. Les quatre premiers attendent une
 * réponse toute faite ; au cinquième, on choisit comment se situer ; au
 * dernier, Hoa demande comment on s'appelle. Le choix du lieu ne s'exécute
 * qu'après : le prénom d'abord, la position ensuite.
 */
const INTRO: Array<{ hoa: string; reponse: string | null }> = [
  { hoa: "Salut ! Je suis Hoa, le dieu des rencontres.", reponse: "Salut Hoa" },
  { hoa: "Mon travail : réunir des inconnus en petites tribus, dans un vrai lieu, près de chez toi.", reponse: "D’accord" },
  { hoa: "Tu choisis une tribu. Vous avez 15 minutes pour vous parler, puis vous vous retrouvez sur place.", reponse: "Cool !" },
  { hoa: "Quand tout le monde est là, je vous dis ce que vous avez en commun. Et je suis libéré.", reponse: "Je comprends" },
  { hoa: "Pour te trouver une tribu près de toi, j’ai besoin de savoir où tu es. Je ne le montre à personne.", reponse: null },
  { hoa: "Dernière chose : comment tu t’appelles ? C’est le nom que ta tribu verra.", reponse: null },
];
const ETAPE_LIEU = 4;
const ETAPE_NOM = 5;
const LIBELLE_LIEU: Record<ChoixLieu, string> = { localiser: "Me localiser", arrondissement: "Choisir mon arrondissement" };

/** Un pseudo acceptable côté téléphone ; le serveur garde le dernier mot. */
export const pseudoValable = (p: string): boolean => p.trim().length >= 2 && p.trim().length <= 24;

export function Intro(props: {
  skin: SkinId;
  pseudo: string;
  onFin: (pseudo: string, choix: ChoixLieu) => void;
  /** Revue depuis Paramètres : on peut la refermer à tout moment. */
  onFermer?: () => void;
}): JSX.Element {
  const [etape, setEtape] = useState(0);
  const [choix, setChoix] = useState<ChoixLieu | null>(null);
  const [nom, setNom] = useState(props.pseudo);
  const fil = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = fil.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [etape]);
  const valider = (): void => {
    if (choix && pseudoValable(nom)) props.onFin(nom.trim(), choix);
  };
  const reponseDe = (i: number): string | null => (i === ETAPE_LIEU ? (choix ? LIBELLE_LIEU[choix] : null) : INTRO[i]!.reponse);
  return (
    <Ecran skin={props.skin} label="Première ouverture">
      {props.onFermer ? (
        <Barre>
          <span className="titre" />
          <button type="button" className="hoa-rond" aria-label="Fermer la présentation" onClick={props.onFermer}>
            <Croix />
          </button>
        </Barre>
      ) : null}
      <div className="hoa-fil" ref={fil}>
        {INTRO.slice(0, etape + 1).map((m, i) => (
          <div key={i} style={{ display: "contents" }}>
            <div className="hoa-rang">
              <Moai expression={i === 0 ? "sourire" : i === ETAPE_LIEU ? "pouce" : i === ETAPE_NOM ? "question" : i % 2 ? "clin" : "sourire"} />
              <Bulle passee={i < etape}>{m.hoa}</Bulle>
            </div>
            {i < etape && reponseDe(i) ? <div className="hoa-mien">{reponseDe(i)}</div> : null}
          </div>
        ))}
        {etape < ETAPE_LIEU ? (
          <div className="hoa-frappe" aria-label="Hoa a encore quelque chose à dire">
            <i />
            <i />
            <i />
          </div>
        ) : null}
      </div>
      <div className="hoa-points" aria-hidden="true">
        {INTRO.map((_, i) => (
          <i key={i} className={i === etape ? "on" : ""} />
        ))}
      </div>
      {etape === ETAPE_LIEU ? (
        <div className="hoa-pile" style={{ marginTop: 0 }}>
          {(["localiser", "arrondissement"] as const).map((c, i) => (
            <button
              key={c}
              type="button"
              className={i === 0 ? "hoa-go" : "hoa-go2"}
              onClick={() => {
                setChoix(c);
                setEtape(ETAPE_NOM);
              }}
            >
              {LIBELLE_LIEU[c]}
            </button>
          ))}
        </div>
      ) : etape === ETAPE_NOM ? (
        // Une zone de texte d'une ligne, hors formulaire : ni barre de
        // remplissage sur Android, ni barre ‹ › ✓ sur iPhone.
        <div className="hoa-pile" style={{ marginTop: 0 }}>
          <div className="hoa-saisie" style={{ marginTop: 0 }}>
            <textarea
              className="hoa-champ hoa-champ-chat"
              aria-label="Pseudo"
              placeholder="Un prénom, un surnom"
              rows={1}
              maxLength={24}
              autoFocus
              autoComplete="off"
              autoCapitalize="words"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="done"
              name="hoa-pseudo"
              value={nom}
              onChange={(e) => setNom(e.target.value.replace(/\n/g, ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  valider();
                }
              }}
            />
          </div>
          <p className="hoa-note" style={{ margin: 0 }}>
            18 ans ou plus.
          </p>
          <button type="button" className="hoa-go" disabled={!pseudoValable(nom)} onClick={valider}>
            C'est moi
          </button>
        </div>
      ) : (
        <button type="button" className="hoa-go" onClick={() => setEtape((e) => e + 1)}>
          {INTRO[etape]!.reponse}
        </button>
      )}
    </Ecran>
  );
}

/* ------------------------------------------------------------------ */
/* Arrondissements : une feuille, une grille (planche H-Arrondissement) */
/* ------------------------------------------------------------------ */

export interface SecteurCompte {
  id: string;
  name: string;
  tribus: number;
}

/** « Lyon 1er (Terreaux) » → « 1er ». */
export function nomCourt(nom: string): string {
  const m = /(\d+\s?(?:er|e|ème))/i.exec(nom);
  return m ? m[1]!.replace(/\s/g, "") : nom;
}

/** « du 2e », « de la régie ». */
export function duSecteur(nom: string): string {
  const c = nomCourt(nom);
  return c !== nom ? `du ${c}` : `de ${nom}`;
}

/** « dans le 2e », « dans la régie ». */
export function dansSecteur(nom: string): string {
  const c = nomCourt(nom);
  return c !== nom ? `dans le ${c}` : `dans ${nom}`;
}

export function FeuilleArrondissements(props: {
  secteurs: SecteurCompte[];
  actuel: string | null;
  message?: string;
  onChoisir: (s: SecteurCompte) => void;
  onFermer?: () => void;
}): JSX.Element {
  return (
    <Feuille label="Choisir un arrondissement" onFermer={props.onFermer}>
      <Parole expression="question">{props.message ?? "Où es-tu ce soir ?"}</Parole>
      <div className="hoa-grille">
        {props.secteurs.map((s) => (
          <button
            key={s.id}
            type="button"
            className={s.id === props.actuel ? "on" : ""}
            aria-pressed={s.id === props.actuel}
            aria-label={`${s.name}, ${s.tribus === 0 ? "aucune tribu" : `${s.tribus} ${pluriel(s.tribus, "tribu", "tribus")}`}`}
            onClick={() => props.onChoisir(s)}
          >
            <b>{nomCourt(s.name)}</b>
            <span>{s.tribus === 0 ? "aucune" : `${s.tribus} ${pluriel(s.tribus, "tribu", "tribus")}`}</span>
          </button>
        ))}
      </div>
    </Feuille>
  );
}

/** L'écran grisé derrière une feuille, quand il n'y a encore rien derrière. */
export function FondGrise(props: { skin: SkinId; lignes: string[]; dessus: ReactNode }): JSX.Element {
  return (
    <Ecran skin={props.skin} label="Ta soirée" dessus={props.dessus}>
      <div className="hoa-grise" aria-hidden="true">
        {props.lignes.map((l) => (
          <span key={l}>{l}</span>
        ))}
      </div>
    </Ecran>
  );
}

/* ------------------------------------------------------------------ */
/* Proposition (H-1-Propose), autres tribus (H-Autres)                  */
/* ------------------------------------------------------------------ */

/** La tribu que Hoa propose : la plus proche dans le temps, puis la plus peuplée. */
export function meilleureTribu(sessions: SessionSummary[]): SessionSummary | null {
  const libres = sessions.filter((s) => s.state !== "closed" && s.participants < s.maxParticipants);
  libres.sort((a, b) => a.startsAt - b.startsAt || b.participants - a.participants);
  return libres[0] ?? null;
}

const NOMBRES = ["Personne", "Une personne", "Deux personnes", "Trois personnes", "Quatre personnes", "Cinq personnes", "Six personnes", "Sept personnes"];

function quiAttend(s: SessionSummary): string {
  if (s.participants === 0) return `Sois le premier : il en faut ${s.minParticipants} pour partir.`;
  const n = NOMBRES[s.participants] ?? `${s.participants} personnes`;
  return `${n} ${s.participants > 1 ? "t'attendent" : "t'attend"}.`;
}

export function BarreSoiree(props: {
  secteur: string;
  initiale: string;
  nPoints: number;
  menuOuvert: boolean;
  onSecteur: () => void;
  onMoi: () => void;
  onPoints: () => void;
  onRetour?: () => void;
}): JSX.Element {
  return (
    <Barre>
      {props.onRetour ? (
        <button type="button" className="hoa-rond" aria-label="Retour" onClick={props.onRetour}>
          <Retour />
        </button>
      ) : null}
      <button type="button" className="lien-titre" onClick={props.onSecteur} aria-label={`Ta soirée, ${props.secteur}. Changer d'arrondissement`}>
        Ta soirée · {nomCourt(props.secteur) === props.secteur ? props.secteur : `Lyon ${nomCourt(props.secteur)}`}
      </button>
      <PastillePoints n={props.nPoints} onClick={props.onPoints} />
      <BoutonMoi initiale={props.initiale} ouvert={props.menuOuvert} onClick={props.onMoi} />
    </Barre>
  );
}

type Communs = {
  skin: SkinId;
  secteur: string;
  initiale: string;
  nPoints: number;
  menuOuvert: boolean;
  onSecteur: () => void;
  onMoi: () => void;
  onPoints: () => void;
  /** Le bouton retour, présent sur chaque écran. */
  onRetour?: () => void;
  dessus?: ReactNode;
};

function barre(p: Communs): JSX.Element {
  return (
    <BarreSoiree
      secteur={p.secteur}
      initiale={p.initiale}
      nPoints={p.nPoints}
      menuOuvert={p.menuOuvert}
      onSecteur={p.onSecteur}
      onMoi={p.onMoi}
      onPoints={p.onPoints}
      onRetour={p.onRetour}
    />
  );
}

function capitale(t: string): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function memeJour(a: number, b: number): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

export function Proposition(
  props: Communs & {
    proposee: SessionSummary;
    autres: number;
    maintenant: number;
    message?: string | null;
    /** Dans l'espace régie : pas de « Créer ». */
    regie?: boolean;
    onJyVais: () => void;
    onUneAutre: () => void;
  },
): JSX.Element {
  const s = props.proposee;
  const quand = jour(s.startsAt, props.maintenant);
  return (
    <Ecran skin={props.skin} label="Proposition" dessus={props.dessus}>
      {barre(props)}
      <div className="hoa-frise">
        <Etape etat="courante" expression="sourire">
          {props.message ? (
            <div className="hoa-erreur" role="alert" style={{ marginBottom: 10 }}>
              {props.message}
            </div>
          ) : null}
          <Bulle>
            {capitale(quand)}, je te propose
            <big>
              {s.color ? <i className="hoa-pastille" style={{ background: s.color }} aria-hidden="true" /> : null}
              {s.subject ?? s.name}
            </big>
            {s.lieu ?? "Rendez-vous"}, {heure(s.startsAt)}. {quiAttend(s)}
          </Bulle>
          <div className="hoa-deux">
            {props.autres > 0 || !props.regie ? (
              <button type="button" className="hoa-go2" onClick={props.onUneAutre}>
                {props.autres > 0 ? "Une autre" : "Créer"}
              </button>
            ) : null}
            <button type="button" className="hoa-go" onClick={props.onJyVais}>
              J'y vais
            </button>
          </div>
        </Etape>
        <EtapesAVenir />
      </div>
    </Ecran>
  );
}

export function Autres(
  props: Communs & {
    sessions: SessionSummary[];
    maintenant: number;
    maTribu: { titre: string; couleur?: string | null; onRevenir: () => void } | null;
    onChoisir: (id: string) => void;
    /** Absent dans l'espace régie : on n'y crée pas de tribu. */
    onCreer?: () => void;
  },
): JSX.Element {
  const liste = [...props.sessions].sort((a, b) => a.startsAt - b.startsAt);
  const vide = props.onCreer ? `Aucune autre tribu ${dansSecteur(props.secteur)} pour l'instant.` : "Aucun évènement disponible pour l'instant.";
  return (
    <Ecran skin={props.skin} label="Les autres tribus" dessus={props.dessus}>
      {barre(props)}
      <div className="hoa-frise">
        <Etape etat="courante" expression="neutre">
          <Bulle>
            {liste.length === 0
              ? vide
              : `Voici ${liste.length === 1 ? "l'autre tribu" : `les ${liste.length} autres tribus`} ${duSecteur(props.secteur)}${memeJour(liste[0]!.startsAt, props.maintenant) ? " ce soir" : ""}.`}
          </Bulle>
          <div className="hoa-liste">
            {props.maTribu ? (
              <button type="button" className="hoa-ligne active" onClick={props.maTribu.onRevenir}>
                {props.maTribu.couleur ? <i className="hoa-pastille" style={{ background: props.maTribu.couleur }} aria-hidden="true" /> : null}
                <div>
                  <b>{props.maTribu.titre}</b>
                  <span>Ta tribu · y revenir</span>
                </div>
              </button>
            ) : null}
            {liste.map((s) => {
              const pleine = s.participants >= s.maxParticipants;
              return (
                <button key={s.id} type="button" className="hoa-ligne" disabled={pleine} onClick={() => props.onChoisir(s.id)}>
                  {s.color ? <i className="hoa-pastille" style={{ background: s.color }} aria-hidden="true" /> : null}
                  <div>
                    <b>{s.subject ?? s.name}</b>
                    <span>
                      {s.lieu ?? s.sectorName} · {memeJour(s.startsAt, props.maintenant) ? "" : `${jour(s.startsAt, props.maintenant)} `}
                      {heure(s.startsAt)}
                    </span>
                  </div>
                  <em aria-label={`${s.participants} sur ${s.maxParticipants}`}>{pleine ? "complet" : `${s.participants}/${s.maxParticipants}`}</em>
                </button>
              );
            })}
            {props.onCreer ? (
              <button type="button" className="hoa-ligne nouvelle" onClick={props.onCreer}>
                Créer la mienne
              </button>
            ) : null}
          </div>
        </Etape>
        <EtapesAVenir />
      </div>
    </Ecran>
  );
}

export function HorsSession(
  props: Communs & {
    message?: string | null;
    planning: Planning | null;
    maintenant: number;
    /** Tribus déjà prêtes dans le quartier (pour la prochaine session). */
    pretes: number;
    onCreer: () => void;
    onPrevenir: () => void;
    onVoir: () => void;
  },
): JSX.Element {
  const p = props.planning?.prochaine && !props.planning.ouvert ? props.planning.prochaine : null;
  const jourP = p ? libelleSession(p.debut, props.maintenant).split(",")[0]!.toLowerCase() : "";
  return (
    <Ecran skin={props.skin} label="Aucune tribu" dessus={props.dessus}>
      {barre(props)}
      <div className="hoa-frise">
        <Etape etat="courante" expression="clin">
          {props.message ? (
            <div className="hoa-erreur" role="alert" style={{ marginBottom: 10 }}>
              {props.message}
            </div>
          ) : null}
          {p ? (
            <>
              <Bulle>
                Je dors {jourP === "ce soir" ? "pour l'instant" : "ce soir"}. Je réunis les prochaines tribus<big>{libelleSession(p.debut, props.maintenant)}</big>
                {props.pretes > 0
                  ? `${capitale(NOMBRES_FEM[props.pretes] ?? String(props.pretes))} ${pluriel(props.pretes, "tribu est déjà prête", "tribus sont déjà prêtes")} ${dansSecteur(props.secteur)}.`
                  : "Crée la première, j'appellerai du monde."}
              </Bulle>
              <button type="button" className="hoa-go" style={{ marginTop: 12 }} onClick={props.onPrevenir}>
                Préviens-moi {jourP}
              </button>
              <button type="button" className="hoa-go2" style={{ marginTop: 10 }} onClick={props.onCreer}>
                Créer une tribu pour {jourP}
              </button>
              {props.pretes > 0 ? (
                <button type="button" className="hoa-lien" onClick={props.onVoir}>
                  Voir les tribus prêtes
                </button>
              ) : null}
            </>
          ) : (
            <>
              <Bulle>
                Je dors pour l'instant : aucune tribu {dansSecteur(props.secteur)}.<big>Ouvre la première</big>j'appelle du monde.
              </Bulle>
              <button type="button" className="hoa-go" style={{ marginTop: 12 }} onClick={props.onCreer}>
                Créer une tribu
              </button>
              <button type="button" className="hoa-go2" style={{ marginTop: 10 }} onClick={props.onSecteur}>
                Un autre quartier
              </button>
            </>
          )}
        </Etape>
        <EtapesAVenir />
      </div>
    </Ecran>
  );
}

/** Après « Quitter la tribu » : planche H-Quitter, avec l'annulation de cinq secondes. */
export function Quitte(
  props: Communs & { restantes: number; annulerDans: number | null; onVoir: () => void; onAnnuler: () => void },
): JSX.Element {
  return (
    <Ecran
      skin={props.skin}
      label="Tribu quittée"
      dessus={
        <>
          {props.annulerDans !== null ? (
            <div className="hoa-toast" role="status">
              <span>Tu as quitté la tribu</span>
              <button type="button" onClick={props.onAnnuler}>
                Annuler · {props.annulerDans} s
              </button>
            </div>
          ) : null}
          {props.dessus}
        </>
      }
    >
      {barre(props)}
      <div className="hoa-frise">
        <Etape etat="courante" expression="pouce" dernier>
          <Bulle>
            C'est noté, je préviens les autres.{" "}
            {props.restantes === 0
              ? `Il ne reste aucune tribu ${dansSecteur(props.secteur)} pour l'instant.`
              : `Il reste ${props.restantes} ${pluriel(props.restantes, "tribu", "tribus")} ${dansSecteur(props.secteur)}.`}
          </Bulle>
          <button type="button" className="hoa-go" style={{ marginTop: 12 }} onClick={props.onVoir}>
            Voir les tribus
          </button>
        </Etape>
      </div>
    </Ecran>
  );
}

/* ------------------------------------------------------------------ */
/* Créer une tribu : creer.tsx (H-Creer)                               */
/* ------------------------------------------------------------------ */

/**
 * Ouvrir une tribu demande un compte avec un e-mail. Donner un pseudo pour
 * rejoindre n'en fait pas un : on garde les thèmes de base et le changement
 * de pseudo, pas la création de tribu ni les personnalisations à débloquer.
 */
export function CompteRequis(props: { skin: SkinId; onCreerCompte: () => void; onRetour: () => void }): JSX.Element {
  return (
    <Ecran skin={props.skin} label="Compte requis">
      <Barre>
        <button type="button" className="hoa-rond" aria-label="Retour" onClick={props.onRetour}>
          <Retour />
        </button>
        <span className="titre">Nouvelle tribu</span>
      </Barre>
      <div className="hoa-frise">
        <Etape etat="courante" expression="question" dernier>
          <Bulle>
            Pour ouvrir une tribu, il te faut un compte avec ton e-⁠mail. C'est lui qui débloque aussi les autres thèmes et
            les autres Hoa.
          </Bulle>
        </Etape>
      </div>
      <div className="hoa-pile" style={{ marginTop: "auto" }}>
        <button type="button" className="hoa-go" onClick={props.onCreerCompte}>
          Créer un compte
        </button>
        <button type="button" className="hoa-go2" onClick={props.onRetour}>
          Retour
        </button>
      </div>
    </Ecran>
  );
}

/* ------------------------------------------------------------------ */
/* Moi (H-Moi), paramètres (H-Parametres), une soirée passée (H-Recap)  */
/* ------------------------------------------------------------------ */

function Engrenage(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3.2" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  );
}

function dateLongue(ms: number): string {
  const t = new Date(ms).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  return capitale(t);
}


function Crayon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4Z" />
      <path d="M13.5 6.5l4 4" />
    </svg>
  );
}

function Coche(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

/** Le pseudo, modifié sur place : crayon, champ, coche. Entrée enregistre, Échap annule. */
function PseudoModifiable(props: { pseudo: string; onPseudo: (p: string) => void }): JSX.Element {
  const [edition, setEdition] = useState(false);
  const [valeur, setValeur] = useState(props.pseudo);
  const propre = valeur.trim();
  const valable = propre.length >= 2 && propre.length <= 24;
  const enregistrer = (): void => {
    if (!valable) return;
    if (propre !== props.pseudo) props.onPseudo(propre);
    setEdition(false);
  };
  if (!edition) {
    return (
      <div className="hoa-pseudo-ligne">
        <h1 className="hoa-grand-titre">{props.pseudo || "Moi"}</h1>
        <button
          type="button"
          className="hoa-rond hoa-crayon"
          aria-label="Modifier le pseudo"
          onClick={() => {
            setValeur(props.pseudo);
            setEdition(true);
          }}
        >
          <Crayon />
        </button>
      </div>
    );
  }
  return (
    <div className="hoa-pseudo-ligne">
      <textarea
        className="hoa-champ hoa-champ-chat hoa-champ-pseudo"
        aria-label="Pseudo"
        rows={1}
        maxLength={24}
        autoFocus
        autoComplete="off"
        autoCapitalize="words"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="done"
        name="hoa-pseudo"
        value={valeur}
        onChange={(e) => setValeur(e.target.value.replace(/\n/g, ""))}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.nativeEvent.isComposing) {
            e.preventDefault();
            enregistrer();
          }
          if (e.key === "Escape") setEdition(false);
        }}
      />
      <button type="button" className="hoa-rond hoa-crayon plein" aria-label="Enregistrer le pseudo" disabled={!valable} onClick={enregistrer}>
        <Coche />
      </button>
      <button type="button" className="hoa-rond hoa-crayon" aria-label="Annuler" onClick={() => setEdition(false)}>
        <Croix />
      </button>
    </div>
  );
}

/** La photo en grand, par-dessus « Mon compte », avec de quoi la changer. */
function PhotoEnGrand(props: {
  photo: string | null;
  pseudo: string;
  envoi: boolean;
  erreur: string | null;
  onPhoto: (photo: string | null) => void;
  onFermer: () => void;
}): JSX.Element {
  const [erreurLocale, setErreurLocale] = useState<string | null>(null);
  useCouvre();
  useEffect(() => {
    const touche = (e: KeyboardEvent): void => {
      if (e.key === "Escape") props.onFermer();
    };
    addEventListener("keydown", touche);
    return () => removeEventListener("keydown", touche);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const erreur = erreurLocale ?? props.erreur;
  return (
    <div className="hoa-photo-grand" role="dialog" aria-modal="true" aria-label="Ma photo">
      <div className="entete">
        <span className="sp" />
        <button type="button" className="hoa-rond" aria-label="Fermer" onClick={props.onFermer}>
          <Croix />
        </button>
      </div>
      <div className="image">
        {props.photo ? <img src={props.photo} alt="Ma photo" /> : <span className="initiale" aria-hidden="true">{(props.pseudo.trim()[0] ?? "?").toUpperCase()}</span>}
      </div>
      {erreur ? (
        <div className="hoa-erreur" role="alert">
          {erreur}
        </div>
      ) : null}
      <label className={props.envoi ? "hoa-go hoa-fichier inactif" : "hoa-go hoa-fichier"}>
        {props.envoi ? "Un instant…" : props.photo ? "Changer la photo" : "Choisir une photo"}
        <input
          type="file"
          accept="image/*"
          disabled={props.envoi}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (!f) return;
            setErreurLocale(null);
            void reduireImage(f).then(props.onPhoto, (x: Error) => setErreurLocale(x.message));
          }}
        />
      </label>
      {props.photo ? (
        <button type="button" className="hoa-lien" disabled={props.envoi} onClick={() => props.onPhoto(null)}>
          Retirer la photo
        </button>
      ) : null}
    </div>
  );
}

export function Moi(props: {
  skin: SkinId;
  pseudo: string;
  /** Nombre de soirées et de rencontres (le détail est dans « Tes soirées »). */
  soirees: number;
  rencontres: number;
  /** Points du compte (serveur) ; sans compte, ceux du téléphone. */
  points: number;
  /** Badges obtenus, `null` sans compte. */
  badges: { obtenus: number } | null;
  photo: string | null;
  /** Un compte (e-mail et mot de passe) est relié à ce téléphone. */
  avecCompte: boolean;
  envoiPhoto: boolean;
  erreurPhoto: string | null;
  dessus?: ReactNode;
  onParametres: () => void;
  onSoirees: () => void;
  onPersonnaliser: () => void;
  onBoutique: () => void;
  onBadges: () => void;
  onPseudo: (p: string) => void;
  onPhoto: (photo: string | null) => void;
  onCreerCompte: () => void;
  onConnexion: () => void;
  onRetour: () => void;
}): JSX.Element {
  const [photoOuverte, setPhotoOuverte] = useState(false);
  return (
    <Ecran
      skin={props.skin}
      label="Moi"
      dessus={
        <>
          {photoOuverte ? (
            <PhotoEnGrand
              photo={props.photo}
              pseudo={props.pseudo}
              envoi={props.envoiPhoto}
              erreur={props.erreurPhoto}
              onPhoto={props.onPhoto}
              onFermer={() => setPhotoOuverte(false)}
            />
          ) : null}
          {props.dessus}
        </>
      }
    >
      <Barre>
        <button type="button" className="hoa-rond" aria-label="Retour" onClick={props.onRetour}>
          <Retour />
        </button>
        <span className="titre" />
        <button type="button" className="hoa-rond" aria-label="Paramètres" onClick={props.onParametres}>
          <Engrenage />
        </button>
      </Barre>
      <div className="hoa-moi-tete">
        <button type="button" className="hoa-photo-bouton" aria-label={props.photo ? "Voir ma photo" : "Ajouter une photo"} onClick={() => setPhotoOuverte(true)}>
          <Avatar src={props.photo} nom={props.pseudo || "?"} taille={72} />
        </button>
        <PseudoModifiable pseudo={props.pseudo} onPseudo={props.onPseudo} />
      </div>
      {!props.avecCompte ? (
        <div className="hoa-deux hoa-compte-actions">
          <button type="button" className="hoa-go2" onClick={props.onConnexion}>
            Se connecter
          </button>
          <button type="button" className="hoa-go" onClick={props.onCreerCompte}>
            Créer un compte
          </button>
        </div>
      ) : null}
      <div className="hoa-nums">
        <button type="button" onClick={props.onBoutique}>
          <b className="avec-fleur">
            <Fleur taille={24} />
            {props.points}
          </b>
          <span>points</span>
        </button>
        <div>
          <b>{props.rencontres}</b>
          <span>{pluriel(props.rencontres, "rencontre", "rencontres")}</span>
        </div>
        <button type="button" onClick={props.onBadges}>
          <b>{props.badges?.obtenus ?? 0}</b>
          <span>badges</span>
        </button>
      </div>
      <div className="hoa-pile">
        <button type="button" className="hoa-go2 hoa-entree" onClick={props.onSoirees}>
          <span>Tes soirées</span>
          <em>{props.soirees}</em>
        </button>
        <button type="button" className="hoa-go2 hoa-entree" onClick={props.onPersonnaliser}>
          <span>Personnaliser</span>
          <em aria-hidden="true">›</em>
        </button>
      </div>
    </Ecran>
  );
}

const NOMBRES_FEM = ["aucune", "une", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf", "dix"];

export function Parametres(props: {
  skin: SkinId;
  /** L'e-mail du compte relié à ce téléphone, ou `null` sans compte. */
  email: string | null;
  onCreerCompte: () => void;
  onConnexion: () => void;
  onDeconnexion: () => void;
  onSupprimer: () => void;
  onRegles: () => void;
  onIntro: () => void;
  onRetour: () => void;
  onExporter: () => void;
  onSignaler: () => void;
  /** Effacer tout ce que Hoa garde sur ce téléphone. */
  onEffacerAppareil: () => void;
  /** Une feuille posée par-dessus (suppression du compte, signalement). */
  dessus?: ReactNode;
}): JSX.Element {
  const [prefs, setPrefs] = useState(lirePreferences);
  const [balise, setBalise] = useState(baliseVoulue);
  return (
    <Ecran skin={props.skin} label="Paramètres" dessus={props.dessus}>
      <Barre>
        <button type="button" className="hoa-rond" aria-label="Retour" onClick={props.onRetour}>
          <Retour />
        </button>
      </Barre>
      <div className="hoa-defile">
        <h1 className="hoa-grand-titre petit">Paramètres</h1>
        <div className="hoa-section">
          <span className="hoa-etiquette">{ESPACES_HUMAINS ? "Me prévenir quand" : "HOA me prévient quand"}</span>
          {/* Espaces Humains : des tables, pas de sessions de quartier. */}
          {(Object.keys(LIBELLES) as Sorte[]).filter((k) => !ESPACES_HUMAINS || k !== "session").map((k) => (
            <div key={k} className="hoa-rangee">
              <span>{ESPACES_HUMAINS ? (LIBELLES_TABLE[k] ?? LIBELLES[k]) : LIBELLES[k]}</span>
              <button
                type="button"
                role="switch"
                aria-checked={prefs[k]}
                aria-label={ESPACES_HUMAINS ? (LIBELLES_TABLE[k] ?? LIBELLES[k]) : LIBELLES[k]}
                className="hoa-interrupteur"
                onClick={() => setPrefs(ecrirePreference(k, !prefs[k]))}
              >
                <i />
              </button>
            </div>
          ))}
        </div>
        <div className="hoa-section">
          <span className="hoa-etiquette">Au rendez-vous</span>
          <div className="hoa-rangee">
            <span>Ma couleur en plein écran à l'arrivée</span>
            <button
              type="button"
              role="switch"
              aria-checked={balise}
              aria-label="Ma couleur en plein écran à l'arrivée"
              className="hoa-interrupteur"
              onClick={() => {
                ecrireBalise(!balise);
                setBalise(!balise);
              }}
            >
              <i />
            </button>
          </div>
        </div>
        <div className="hoa-section">
          <span className="hoa-etiquette">{ESPACES_HUMAINS ? "Cet appareil" : "Mon compte"}</span>
          {ESPACES_HUMAINS ? null : props.email ? (
            <>
              <div className="hoa-rangee">
                <span className="hoa-nowrap">E-mail</span>
                <span className="hoa-valeur">{props.email}</span>
              </div>
              <button type="button" className="hoa-rangee" onClick={props.onExporter}>
                Télécharger mes données
              </button>
              <button type="button" className="hoa-rangee" onClick={props.onDeconnexion}>
                Se déconnecter de ce téléphone
              </button>
              <button type="button" className="hoa-rangee" onClick={props.onSupprimer}>
                Supprimer mon compte
              </button>
            </>
          ) : (
            <>
              <button type="button" className="hoa-rangee" onClick={props.onCreerCompte}>
                Créer un compte
              </button>
              <button type="button" className="hoa-rangee" onClick={props.onConnexion}>
                Se connecter
              </button>
            </>
          )}
          <button type="button" className="hoa-rangee" onClick={props.onEffacerAppareil}>
            Effacer les données de cet appareil
          </button>
        </div>
        <div className="hoa-section">
          <span className="hoa-etiquette">Informations</span>
          <button type="button" className="hoa-rangee" onClick={props.onRegles}>
            Règles, confidentialité, mentions légales
          </button>
          <button type="button" className="hoa-rangee" onClick={props.onSignaler}>
            Signaler un problème
          </button>
          {ESPACES_HUMAINS ? null : (
            <button type="button" className="hoa-rangee" onClick={props.onIntro}>
              Revoir la présentation de Hoa
            </button>
          )}
        </div>
      </div>
    </Ecran>
  );
}

export function Recap(props: {
  skin: SkinId;
  soiree: SoireePassee;
  dessus?: ReactNode;
  onRetour: () => void;
  onRelire: () => void;
  onSignaler: () => void;
}): JSX.Element {
  const s = props.soiree;
  const total = s.reunis ? Math.max(s.personnes, s.avec.length + 1) : s.personnes;
  const messages = s.chat?.length ?? 0;
  return (
    <Ecran skin={props.skin} label="Une soirée" dessus={props.dessus}>
      <Barre>
        <button type="button" className="hoa-rond" aria-label="Retour" onClick={props.onRetour}>
          <Retour />
        </button>
        <span className="titre">{dateLongue(s.rdvAt)}</span>
      </Barre>
      <div className="hoa-frise">
        <Etape etat="faite">
          <Libelle>Tribu rejointe à {heure(s.rejointA)}</Libelle>
        </Etape>
        {messages > 0 ? (
          <Etape etat="faite">
            <span className="hoa-ligne-etape">
              <Libelle>
                Chat · {messages} {pluriel(messages, "message", "messages")}
              </Libelle>
              <button type="button" className="hoa-relire" onClick={props.onRelire}>
                Relire
              </button>
            </span>
          </Etape>
        ) : null}
        <Etape etat="faite">
          <Libelle>
            Rendez-vous, {s.lieu}, {heure(s.rdvAt)}
          </Libelle>
        </Etape>
        <Etape etat="courante" dernier libre={s.reunis} expression={s.reunis ? "rire" : "neutre"}>
          <Bulle>
            {s.reunis ? (
              <>
                Ce soir-là, vous étiez {NOMBRES_FEM[total] ?? total}.
                {s.pointCommun ? (
                  <>
                    {" "}Vous étiez d'accord sur<big>{s.pointCommun}</big>
                  </>
                ) : null}
                {s.gain ? ` +${s.gain} points pour toi.` : ""}
              </>
            ) : (
              <>Ce soir-là, la tribu ne s'est pas retrouvée.</>
            )}
          </Bulle>
          {s.avec.length > 0 ? (
            <div className="hoa-membres">
              {s.avec.map((p) => (
                <span key={p}>{p}</span>
              ))}
            </div>
          ) : null}
        </Etape>
      </div>
      <button type="button" className="hoa-go2" style={{ marginTop: "auto" }} onClick={props.onSignaler}>
        Signaler un problème
      </button>
    </Ecran>
  );
}
